import { ref } from 'vue';
import type { FormValidationResult } from '../types/formEngine.types';
import { showToast } from '../../../utils/toastHelpers';
import { hasOTPField, getOTPField, findLinkedEmailField, findSectionIndexForField } from '../../../types/formUtils';
import type { FormStructure, FormField, Section } from '../../../types/formTypeStructure';

// Fonction utilitaire pour calculer la prochaine section (sans naviguer)
function getNextSectionIndex(
  currentSectionIndex: number,
  currentFieldPage: number,
  form: FormStructure,
  paginationMode: 'byFields' | 'bySection',
  fieldsPerPage: number = 4
): number {
  if (paginationMode === 'bySection') {
    // Mode bySection : prochaine section directement
    if (currentSectionIndex < form.sections.length - 1) {
      return currentSectionIndex + 1;
    }
    return -1; // Pas de prochaine section
  }

  // Mode byFields : calculer si on passe à la prochaine section
  const currentSection = form.sections[currentSectionIndex];
  const totalFieldPages = Math.ceil((currentSection.formFields?.length || 0) / fieldsPerPage);
  
  if (currentFieldPage < totalFieldPages - 1) {
    // Reste dans la même section
    return currentSectionIndex;
  } else if (currentSectionIndex < form.sections.length - 1) {
    // Passe à la prochaine section
    return currentSectionIndex + 1;
  }
  
  return -1; // Pas de prochaine section
}

// Fonction pour envoyer l'OTP (réutiliser la logique de useOTPHandler)
async function sendOTPBeforeNavigation(
  otpField: FormField,
  formValues: Record<string, any>,
  allSections: Section[]
): Promise<void> {
  const options = otpField.otpFormFieldExecOptions;
  if (!options) {
    throw new Error("OTP options are not configured for this field");
  }

  // Utiliser sendOTPApiConfig si disponible, sinon utiliser la config par défaut
  const sendConfig = options.sendOTPApiConfig ?? {
    serverDns: options.serverDns,
    postApiEndPoint: options.postApiEndPoint,
    payload: options.payload,
    extraHeaders: options.extraHeaders,
    bearer: options.bearer,
  };

  // Trouver le champ lié (peut être un email, un ID, etc.)
  const otpSectionIndex = findSectionIndexForField(otpField.fieldName, allSections);
  const linkedField = findLinkedEmailField(otpField, allSections, otpSectionIndex);
  
  if (!linkedField) {
    const linkedFieldName = options.linkedEmailFieldName || 'unknown';
    throw new Error(
      `Linked field "${linkedFieldName}" not found for OTP field "${otpField.fieldName}". ` +
      `Make sure the field exists in a section before the OTP section.`
    );
  }

  const linkedFieldValue = formValues[linkedField.fieldName];
  if (!linkedFieldValue) {
    throw new Error(
      `Linked field "${linkedField.fieldName}" has no value. ` +
      `Please fill this field before proceeding to the OTP section.`
    );
  }

  // Construire l'URL
  const base = sendConfig.serverDns.replace(/\/+$/, "");
  const endpoint = sendConfig.postApiEndPoint.replace(/^\/+/, "");
  const url = `${base}/${endpoint}`;

  // Normaliser et fusionner le payload
  const basePayload = sendConfig.payload || {};
  const mergedPayload = {
    ...basePayload,
    ...formValues, // Fusionner avec les données du formulaire
  };

  // Préparer les headers
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(sendConfig.extraHeaders ?? {}),
  };

  if (sendConfig.bearer) {
    headers.Authorization = `Bearer ${sendConfig.bearer}`;
  }

  // Envoyer la requête POST avec timeout
  const controller = new AbortController();
  const timeoutMs = options.timeoutMs || 15000;
  
  const fetchPromise = fetch(url, {
    method: "POST",
    headers,
    body: JSON.stringify(mergedPayload),
    signal: controller.signal,
  });

  // Gérer le timeout
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  
  try {
    const response = await fetchPromise;
    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
      throw new Error(errorData?.message || `OTP request failed with status ${response.status}`);
    }

    const responseData = await response.json();
    
    // Vérifier la réponse
    if (!responseData.success && response.status !== 200) {
      throw new Error(responseData?.message || 'Failed to send OTP');
    }

    console.log('✅ [OTP] OTP envoyé avec succès avant navigation:', responseData);
  } catch (error: any) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error('OTP request timeout');
    }
    throw error;
  }
}

export function useFormNavigationActions(
  navigation: any,
  validatePage: () => Promise<FormValidationResult>,
  syncFormWithStore?: () => Promise<void>,
  form?: FormStructure,
  formValues?: Record<string, any>,
  paginationMode?: 'byFields' | 'bySection',
  handleFinalSubmit?: () => Promise<void>
) {
  const isValidating = ref(false);

  const handleNext = async () => {
    isValidating.value = true;
    try {
      // Si on est sur la section de prévisualisation, soumettre le formulaire
      if (navigation.isPreviewSection?.value && handleFinalSubmit) {
        await handleFinalSubmit();
        return;
      }

      const result = await validatePage();
      
      // Vérifier d'abord si le formulaire est valide globalement
      if (!result.isValid) {
        // Afficher les erreurs détaillées par champ
        if (result.invalidFields && result.invalidFields.length > 0) {
          result.invalidFields.forEach(field => {
            field.errors.forEach(error => {
              showToast(`${field.fieldLabel} : ${error}`, 'error');
            });
          });
          return;
        }
        
        if (result.emptyRequiredFields && result.emptyRequiredFields.length > 0) {
          result.emptyRequiredFields.forEach(field => {
            showToast(`${field.fieldLabel} : ${field.errors[0]}`, 'error');
          });
          return;
        }
        
        if (result.untouchedRequiredFields && result.untouchedRequiredFields.length > 0) {
          result.untouchedRequiredFields.forEach(field => {
            showToast(`${field.fieldLabel} : ${field.errors[0]}`, 'error');
          });
          return;
        }
        
        // Si aucune erreur spécifique n'est trouvée mais isValid est false
        showToast('Certains champs contiennent des erreurs. Veuillez les corriger avant de continuer.', 'error');
        return;
      }
      
      // 🆕 DÉTECTION ET ENVOI OTP AVANT NAVIGATION
      if (form && formValues && paginationMode) {
        const currentSectionIndex = navigation.sectionIndex.value;
        const currentFieldPage = navigation.fieldPage.value;
        const nextSectionIndex = getNextSectionIndex(
          currentSectionIndex,
          currentFieldPage,
          form,
          paginationMode
        );

        if (nextSectionIndex !== -1 && nextSectionIndex !== currentSectionIndex) {
          const nextSection = form.sections[nextSectionIndex];
          
          // Vérifier si la prochaine section contient un champ OTP
          if (hasOTPField(nextSection)) {
            const otpField = getOTPField(nextSection);
            
            if (otpField && otpField.otpFormFieldExecOptions) {
              try {
                console.log('🔄 [Navigation] Détection section OTP, envoi de l\'OTP avant navigation...', {
                  nextSectionIndex,
                  otpFieldName: otpField.fieldName,
                  linkedField: otpField.otpFormFieldExecOptions.linkedEmailFieldName
                });

                // Envoyer l'OTP avant de naviguer
                await sendOTPBeforeNavigation(otpField, formValues, form.sections);
                
                console.log('✅ [Navigation] OTP envoyé avec succès, navigation vers section OTP...');
                showToast('Code de vérification envoyé ! 📧', 'success');
              } catch (error: any) {
                console.error('❌ [Navigation] Erreur lors de l\'envoi de l\'OTP:', error);
                showToast(
                  `Erreur lors de l'envoi du code de vérification: ${error.message || 'Erreur inconnue'}`,
                  'error'
                );
                // Ne pas naviguer si l'envoi échoue
                return;
              }
            }
          }
        }
      }
      
      // Si tout est OK, navigation normale
      console.log('🚀 [Navigation] Avant goToNext:', {
        sectionIndex: navigation.sectionIndex.value,
        fieldPage: navigation.fieldPage.value,
        currentSection: navigation.currentSection.value?.sectionId,
        pagedFieldsCount: navigation.pagedFields.value?.length
      });
      
      navigation.goToNext();
      
      console.log('🚀 [Navigation] Après goToNext:', {
        sectionIndex: navigation.sectionIndex.value,
        fieldPage: navigation.fieldPage.value,
        currentSection: navigation.currentSection.value?.sectionId,
        pagedFieldsCount: navigation.pagedFields.value?.length,
        pagedFields: navigation.pagedFields.value?.map((f: any) => f.fieldName)
      });
      
      // Synchroniser avec le store après navigation
      if (syncFormWithStore) {
        await syncFormWithStore();
      }
      
      showToast(`Super section suivante ! 🎉 (${result.requiredFieldsCount}/${result.visibleFieldsCount} champs requis)`, 'success');
    } finally {
      isValidating.value = false;
    }
  };

  const handlePrevious = async () => {
    // Navigation normale
    navigation.goToPrevious();
    
    // Synchroniser avec le store après navigation
    if (syncFormWithStore) {
      await syncFormWithStore();
    }
    
    showToast('⬅️ Retour à la section précédente. Vos données ont été conservées.', 'info');
  };

  return {
    handleNext,
    handlePrevious,
    isValidating
  };
}

